import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { GuideGoal, PeptideEntry, StackEntry } from './peptides101.types';
import { trackEvent } from '../../utils/analytics';
import { Citations } from '../../components/Citations';

const Chevron = () => (
  <svg className="lp-acc-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

const PeptideRow: React.FC<{ entry: PeptideEntry; goalId: string }> = ({ entry, goalId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="lp-acc-row" data-open={open}>
      <button
        className="lp-acc-head"
        aria-expanded={open}
        onClick={() => {
          const nextOpen = !open;
          setOpen(nextOpen);
          if (nextOpen) void trackEvent('learn_peptide_expanded', { goal: goalId, slug: entry.slug });
        }}
      >
        <span>
          <span className="lp-acc-name">{entry.name}</span>
          <span className="lp-acc-hook">{entry.hook}</span>
        </span>
        <Chevron />
      </button>
      {open && (
        <div className="lp-acc-body">
          <h4>How it works</h4>
          <p>{entry.howItWorks}</p>
          <h4>How people take it</h4>
          <p>{entry.howTaken}</p>
          {entry.honestyNote && <div className="lp-acc-note">{entry.honestyNote}</div>}
          {/* App Review 1.4.1: the mechanism and how-people-take-it text above
              are medical claims about a named substance, so their sources are
              shown right here rather than only on the library page. */}
          <Citations sources={entry.sources} variant="block" />
          <Link className="lp-acc-link" to={`/library/${entry.slug}`}>Full details in the library</Link>
        </div>
      )}
    </div>
  );
};

const StackRow: React.FC<{ entry: StackEntry }> = ({ entry }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="lp-acc-row" data-open={open}>
      <button className="lp-acc-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span>
          <span className="lp-acc-name">{entry.name}</span>
          <span className="lp-acc-hook">{entry.hook}</span>
        </span>
        <Chevron />
      </button>
      {open && (
        <div className="lp-acc-body">
          <p>{entry.blurb}</p>
          <Link className="lp-acc-link" to={`/library/stack/${entry.slug}`}>Full details in the library</Link>
        </div>
      )}
    </div>
  );
};

export const GoalAccordion: React.FC<{ goal: GuideGoal }> = ({ goal }) => {
  const [stacksShown, setStacksShown] = useState(false);
  return (
    <div>
      <p className="lp-goal-intro">{goal.intro}</p>
      {goal.peptides.map((p) => (
        <PeptideRow key={p.slug} entry={p} goalId={goal.id} />
      ))}
      {goal.stacks.length > 0 && !stacksShown && (
        <button
          className="lp-reveal"
          onClick={() => {
            setStacksShown(true);
            void trackEvent('learn_stacks_revealed', { goal: goal.id });
          }}
        >
          Want to go further? See combos people stack
        </button>
      )}
      {stacksShown && goal.stacks.map((s) => <StackRow key={s.slug} entry={s} />)}
    </div>
  );
};
